import { supabase } from '../lib/supabase';

export type AIGenerationType = 'ESTIMATE_PROPOSAL' | 'GENERATE_PROPERTY_NAME' | 'PARSE_LEBONCOIN';

interface AIGenerationParameters {
  // Pour ESTIMATE_PROPOSAL
  type?: string;
  city?: string;
  surface?: number;
  
  // Pour PARSE_LEBONCOIN
  html?: string;
}

interface AIGenerationResponse {
  text: string;
  inputTokens: number;
  outputTokens: number;
}

/**
 * Service pour appeler l'IA via Edge Function Supabase
 * Les prompts sont gérés côté serveur pour la sécurité
 */
export const aiService = {
  /**
   * Génère une réponse IA selon le type spécifié
   */
  generate: async (
    type: AIGenerationType,
    parameters: AIGenerationParameters
  ): Promise<AIGenerationResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-generate', {
        body: {
          type,
          parameters,
        },
      });

      if (error) {
        console.error('Erreur lors de l\'appel IA:', error);
        throw new Error(error.message || 'Erreur lors de la génération IA');
      }

      if (!data || !data.text) {
        throw new Error('Réponse invalide de l\'API IA');
      }

      return {
        text: data.text,
        inputTokens: data.inputTokens || 0,
        outputTokens: data.outputTokens || 0,
      };
    } catch (e: any) {
      console.error('Erreur critique lors de l\'appel IA:', e);
      throw new Error(e.message || 'Erreur inconnue lors de la génération IA');
    }
  },

  /**
   * Estime le prix de nuitée et le taux d'occupation pour une proposition
   */
  estimateProposal: async (
    parameters: {
      address: string;
      propertyType: string;
      city: string;
      surface: number;
      rooms?: number;
      bedrooms?: number;
      bathrooms?: number;
      maxGuests?: number;
      guestCount?: number;
    }
  ): Promise<{ nightlyRate: number; occupancyRate: number }> => {
    const response = await aiService.generate('ESTIMATE_PROPOSAL', {
      address: parameters.address,
      type: parameters.propertyType,
      propertyType: parameters.propertyType,
      city: parameters.city,
      surface: parameters.surface,
      bedrooms: parameters.bedrooms,
      bathrooms: parameters.bathrooms,
      maxGuests: parameters.maxGuests,
      guestCount: parameters.guestCount || parameters.maxGuests,
    });

    const cleanJson = response.text.replace(/```json|```/g, '').trim();
    const data = JSON.parse(cleanJson);
    
    return {
      nightlyRate: data.nightlyRate || data.averageNightlyPrice || 0,
      occupancyRate: data.occupancyRate || 0,
    };
  },

  /**
   * Génère 3 noms commerciaux pour une propriété
   */
  generatePropertyName: async (
    propertyType: string,
    city: string,
    surface: number,
    bedrooms: number
  ): Promise<string[]> => {
    const response = await aiService.generate('GENERATE_PROPERTY_NAME', {
      type: propertyType,
      city,
      surface,
      bedrooms,
    });

    try {
      const cleanJson = response.text.replace(/```json|```/g, '').trim();
      const data = JSON.parse(cleanJson);
      if (data.names && Array.isArray(data.names) && data.names.length >= 3) {
        return data.names.slice(0, 3);
      }
      // Fallback si le format n'est pas correct
      const lines = response.text.trim().split('\n').filter(line => line.trim());
      if (lines.length >= 3) {
        return lines.slice(0, 3).map(line => line.replace(/^[-*•]\s*/, '').trim());
      }
      // Si on n'a qu'un seul nom, on retourne 3 fois le même
      return [response.text.trim(), response.text.trim(), response.text.trim()];
    } catch (e) {
      console.error('Error parsing property names:', e);
      // Fallback: essayer d'extraire 3 noms séparés par des retours à la ligne ou virgules
      const text = response.text.trim();
      const names = text.split(/[\n,;]/).map(n => n.trim()).filter(n => n.length > 0);
      if (names.length >= 3) {
        return names.slice(0, 3);
      }
      // Dernier recours: retourner le texte tel quel 3 fois
      return [text, text, text];
    }
  },

  /**
   * Parse une annonce Leboncoin depuis le HTML
   */
  parseLeboncoin: async (html: string): Promise<any> => {
    const response = await aiService.generate('PARSE_LEBONCOIN', { html });

    const cleanJson = response.text.replace(/```json|```/g, '').trim();
    return JSON.parse(cleanJson);
  },
};
